// 058A — Escalas de Valoración para Cuidados Paliativos (Fase 5, Tanda 7). Las 6
// escalas y las decisiones de puntuación están documentadas en
// `escalasPaliativosDatos.ts` (fuente: PDF páginas 214-216). El bloque A lo
// resuelve `EncabezadoMspCampos`, igual que 058/052/016.
//
// Sin FHIR a propósito: las escalas no tienen consumidor demostrable hoy; quedan
// como `Par[]` en el PDF firmado y su resultado se transcribe en el bloque M de 058.
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { Bloque, CamposSimples, SelectSimple } from './camposSimples';
import { ProfesionalPorTurno } from './ProfesionalPorTurno';
import { CAMPOS_PROFESIONAL_PAPEL } from './profesionalPorTurnoDatos';
import { calcularTotalEscala, EscalaClinica } from './uci/EscalaClinica';
import {
  CAMPOS_DME,
  CORTE_DME,
  DEFINICION_BARTHEL,
  DEFINICION_GIJON,
  DEFINICION_ZARIT,
  OPCIONES_CATEGORIA_PFEIFFER,
  OPCIONES_KPS_PPS,
  columnasPps,
  definicionPfeiffer,
  estadoInicialEscalasPaliativos,
  HOJAS_PROFESIONAL_058A,
  filaKpsPps,
  paresDeEscalasPaliativos,
  totalDme,
  type EstadoEscalasPaliativos,
} from './escalasPaliativosDatos';

const CODIGO = '058A';

function CampoFecha({ id, etiqueta, valor, onChange }: { readonly id: string; readonly etiqueta: string; readonly valor: string; readonly onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="flex max-w-xs flex-col gap-1">
      <Label htmlFor={id}>{etiqueta}</Label>
      <Input id={id} type="date" value={valor} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export function Formulario058A({ subject, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('hub.formulario.058A');
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): Escalas de cuidados paliativos: el papel no trae servicio ni fija ámbito.
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo, { sinServicio: true });

  const [estado, setEstado] = useState<EstadoEscalasPaliativos>(() => estadoInicialEscalasPaliativos());
  const set = (parcial: Partial<EstadoEscalasPaliativos>): void => setEstado({ ...estado, ...parcial });

  const kps = filaKpsPps(estado.kpsPps);
  const defPfeiffer = definicionPfeiffer(estado.pfeifferSinTelefono);
  const totalPfeiffer = calcularTotalEscala(defPfeiffer, estado.pfeiffer);
  const dme = totalDme(estado.dme);

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      onGuardado(paresDeEscalasPaliativos(t, estado), datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloque titulo={t('formularios.f058a.kpsPpsTitulo')}>
        <SelectSimple id="f058a-kps" etiqueta={t('formularios.f058a.kpsPpsEtiqueta')} valor={estado.kpsPps} opciones={OPCIONES_KPS_PPS} onChange={(v) => set({ kpsPps: v })} />
        {kps && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-border p-3 text-sm sm:grid-cols-3">
            {columnasPps(kps).map((c) => (
              <div key={c.etiqueta}>
                <dt className="text-xs text-hce-muted">{c.etiqueta}</dt>
                <dd>{c.valor}</dd>
              </div>
            ))}
          </dl>
        )}
      </Bloque>

      <Bloque titulo={t('formularios.f058a.barthelTitulo')}>
        <CampoFecha id="f058a-barthel-fecha" etiqueta={t('formularios.f058a.fecha')} valor={estado.barthelFecha} onChange={(v) => set({ barthelFecha: v })} />
        <EscalaClinica idPrefix="f058a-barthel" definicion={DEFINICION_BARTHEL} respuestas={estado.barthel} onChange={(r) => set({ barthel: r })} />
      </Bloque>

      <Bloque titulo={t('formularios.f058a.pfeifferTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f058a.pfeifferNota')}</p>
        <CampoFecha id="f058a-pfeiffer-fecha" etiqueta={t('formularios.f058a.fecha')} valor={estado.pfeifferFecha} onChange={(v) => set({ pfeifferFecha: v })} />
        <div className="flex items-center gap-2">
          <Checkbox id="f058a-pfeiffer-sin-telefono" checked={estado.pfeifferSinTelefono} onCheckedChange={(v) => set({ pfeifferSinTelefono: v === true })} />
          <Label htmlFor="f058a-pfeiffer-sin-telefono" className="font-normal">
            {t('formularios.f058a.pfeifferSinTelefono')}
          </Label>
        </div>
        <EscalaClinica idPrefix="f058a-pfeiffer" definicion={defPfeiffer} respuestas={estado.pfeiffer} onChange={(r) => set({ pfeiffer: r })} />
        {totalPfeiffer !== undefined && <p className="text-sm font-medium text-hce-ink">{t('formularios.f058a.pfeifferTotalErrores', { total: totalPfeiffer })}</p>}
        <SelectSimple id="f058a-pfeiffer-categoria" etiqueta={t('formularios.f058a.categoriaPfeiffer')} valor={estado.pfeifferCategoria} opciones={OPCIONES_CATEGORIA_PFEIFFER} onChange={(v) => set({ pfeifferCategoria: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f058a.dmeTitulo')}>
        <CamposSimples prefijoId="f058a-dme" campos={CAMPOS_DME} valores={estado.dme} columnas={2} onChange={(v) => set({ dme: v })} />
        <p className="text-xs text-hce-muted">{t('formularios.f058a.dmeCriterio')}</p>
        {dme !== undefined && (
          <p className="text-sm font-medium text-hce-ink">
            {t('formularios.f058a.dmeResultado', { total: dme })} — {dme >= CORTE_DME ? t('formularios.f058a.dmePositivo') : t('formularios.f058a.dmeNegativo')}
          </p>
        )}
      </Bloque>

      <Bloque titulo={t('formularios.f058a.gijonTitulo')}>
        <CampoFecha id="f058a-gijon-fecha" etiqueta={t('formularios.f058a.fecha')} valor={estado.gijonFecha} onChange={(v) => set({ gijonFecha: v })} />
        <EscalaClinica idPrefix="f058a-gijon" definicion={DEFINICION_GIJON} respuestas={estado.gijon} onChange={(r) => set({ gijon: r })} />
      </Bloque>

      <Bloque titulo={t('formularios.f058a.zaritTitulo')}>
        <p className="text-sm text-hce-ink">{t('formularios.f058a.zaritIntro')}</p>
        <p className="text-xs text-hce-muted">{t('formularios.f058a.zaritNota')}</p>
        <EscalaClinica idPrefix="f058a-zarit" definicion={DEFINICION_ZARIT} respuestas={estado.zarit} onChange={(r) => set({ zarit: r })} />
      </Bloque>

      <ProfesionalPorTurno
        prefijoI18n="formularios.f058a.profesionalHojas"
        prefijoId="f058a-prof"
        campos={CAMPOS_PROFESIONAL_PAPEL}
        turnos={HOJAS_PROFESIONAL_058A}
        estado={estado.profesional}
        onChange={(p) => set({ profesional: p })}
      />

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
